import { BRAND } from '@convometry/contracts';
import { Button, Card, FormField, Input, Wordmark } from '@convometry/ui';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Lock } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { usePageTitle } from '../app/title.js';
import { api, ApiError, signIn } from '../lib/api.js';
import { qk } from '../lib/query.js';

/** Only allow same-app relative redirects after sign-in. */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/iniciar-sesion')) return '/';
  return next;
}

export function SignInPage() {
  usePageTitle('Iniciar sesión');
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const expired = sp.get('expirada') === '1';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      qc.removeQueries();
      await qc.fetchQuery({ queryKey: qk.me, queryFn: () => api('me') });
      navigate(safeNext(sp.get('siguiente')), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.kind === 'unauthorized' || err.kind === 'validation')) {
        setError('Correo o contraseña incorrectos.');
      } else if (err instanceof ApiError && err.kind === 'rate_limited') {
        setError('Demasiados intentos. Espera unos minutos antes de volver a intentar.');
      } else if (err instanceof ApiError && err.kind === 'forbidden') {
        setError('Tu cuenta no tiene acceso. Contacta a un propietario del negocio.');
      } else {
        setError(err instanceof Error ? err.message : 'No pudimos iniciar sesión.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="grid min-h-dvh grid-rows-[1fr_auto] bg-surface-canvas px-4">
      <div className="mx-auto grid w-full max-w-[420px] content-center gap-6 py-10">
        <div className="grid justify-items-center gap-3 text-center">
          <Wordmark size="lg" />
          <p className="text-body text-text-secondary">{BRAND.tagline}</p>
        </div>
        <Card as="section" padding="lg" aria-labelledby="signin-title">
          <h1 id="signin-title" className="mb-1 text-card-title font-semibold text-text-primary">
            Iniciar sesión
          </h1>
          <p className="mb-5 text-body text-text-secondary">Accede con la cuenta que te asignó tu negocio.</p>
          {expired && !error ? (
            <p role="status" className="mb-4 flex items-start gap-2 rounded-menu bg-info-bg p-3 text-helper text-info">
              <Lock aria-hidden className="mt-0.5 size-4 shrink-0" />
              Tu sesión expiró. Inicia sesión de nuevo para continuar.
            </p>
          ) : null}
          <form className="grid gap-4" onSubmit={(e) => void submit(e)} noValidate>
            <FormField label="Correo electrónico" required>
              <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </FormField>
            <FormField label="Contraseña" required>
              <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </FormField>
            {error ? (
              <p role="alert" className="flex items-start gap-2 text-helper font-medium text-danger">
                <AlertCircle aria-hidden className="mt-0.5 size-4 shrink-0" />
                {error}
              </p>
            ) : null}
            <Button type="submit" loading={loading} disabled={!email || !password}>
              Iniciar sesión
            </Button>
          </form>
          <p className="mt-4 text-helper text-text-secondary">
            ¿Olvidaste tu contraseña? Pide a un propietario o administrador que la restablezca. No hay registro público.
          </p>
        </Card>
      </div>
      <footer className="py-6 text-center text-helper text-text-secondary">
        {BRAND.name} {BRAND.endorsement} · {BRAND.descriptor}
      </footer>
    </main>
  );
}
